import {
  AfterViewInit,
  Component,
  EventEmitter,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  input
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import WhitelabelIosConfigFactory from '../../factories/whitelabel-ios-config-factory';
import AzureService from 'src/app/shared/services/azure.service';
import {
  IOS_CREDENTIALS_DEFAULT_FOLDER,
  IOS_GOOGLE_SERVICE_INFO_PLIST_NAME,
  IOS_ICONS_DEFAULT_FOLDER,
  WhitelabelIosIcons,
  IWhitelabelIosPayload,
} from '../../models/whitelabel-ios-config';

import WhitelabelUploadHelper from '../../helpers/upload.helper';
import { Observable, Subject, forkJoin } from 'rxjs';
import {
  IWhitelabelIosPropertiesConfiguration,
  IWhitelabelOnesignalIosPropertiesConfiguration,
  LOGO_DEFAULT_NAME,
  WhitelabelConfiguration,
} from '../../models/whitelabel-config';

import * as xml2js from 'xml2js';
import { WhitelabelService } from '../../services/whitelabel.service';
import { ToastrService } from 'ngx-toastr';
import { environment } from 'src/environments/environment';
import { prepareIconsLinksToForm } from '../../helpers/file.helper';
import { guidValidator } from '../../helpers/onesignal-validators';

@Component({
  standalone: false,
  selector: 'app-whitelabel-ios-component',
  templateUrl: './whitelabel-ios.component.html',
  styleUrls: ['./whitelabel-ios.component.css'],
})
export class WhitelabelIosComponent implements OnInit, OnChanges {
  readonly parentForm = input.required<FormGroup>();

  readonly isIosChecked = input.required<boolean>();

  readonly savedConfigurations = input<WhitelabelConfiguration>();

  readonly isDraftMode = input.required<boolean>();

  public iosForm: FormGroup;

  public whitelabelUploadHelper: WhitelabelUploadHelper;

  public tabs: 0 | 1 = 0;

  constructor(
    private formBuilder: FormBuilder,
    private azureService: AzureService,
    private whitelabelService: WhitelabelService,
    private toastrService: ToastrService
  ) {
    this.whitelabelUploadHelper = new WhitelabelUploadHelper(this.azureService);

    this.iosForm = this.formBuilder.group({
      developmentTeam: new FormControl(null, [
        Validators.required,
        Validators.maxLength(10),
        Validators.minLength(10),
      ]),
      status: new FormControl(0, Validators.required),
      appId: new FormControl(null, Validators.required),
      iosBundleId: new FormControl(null, Validators.required),
      applicationGroup: new FormControl(null, Validators.required),
      apiKeyId: new FormControl(null, Validators.required),
      apiKeyIssuerId: new FormControl(null, [Validators.required, guidValidator]),
      apiTokenBase64: new FormControl(null, Validators.required),
      oneSignalBundleId: new FormControl(null, Validators.required),
      appProvisionProfileSpecifier: new FormControl(null, Validators.required),
      onesignalProvisionProfileSpecifier: new FormControl(
        null,
        Validators.required
      ),

      appProvisionProfileLink: new FormControl(null, Validators.required),
      oneSignalProvisionProfileLink: new FormControl(null, Validators.required),
      p12CertificateLink: new FormControl(null, Validators.required),
      iconsLink: new FormControl(null, Validators.required),
      appIconLink: new FormControl(null, Validators.required),
      googleServicePlistLink: new FormControl(null, Validators.required),
      iosCertificateExpiryDate: new FormControl(null),
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    const { isIosChecked, savedConfigurations, isDraftMode } = changes;

    if (isIosChecked && isIosChecked.currentValue) {
      this.parentForm().addControl('iosForm', this.iosForm);
    } else if (isIosChecked && !isIosChecked.currentValue) {
      this.parentForm().removeControl('iosForm');
    }

    if (isDraftMode && isDraftMode.currentValue) {
      this.handleEnableDraftModeControls();
    } else if (isDraftMode && !isDraftMode.currentValue) {
      this.handleDisableDraftModeControls();
    }

    if (
      this.parentForm().controls['iosForm'] &&
      savedConfigurations?.currentValue
    ) {
      const _savedConfigurations =
        savedConfigurations.currentValue as WhitelabelConfiguration;
      this.updateIosValuesWithPreviousSavedConfigurations(
        _savedConfigurations.ios
      );
    }
  }

  ngOnInit(): void {
    this.handleAccountantId();

    this.iosForm.get('iosBundleId')?.valueChanges.subscribe((newValue) => {
      const groupValue = `group.${newValue}`;
      this.iosForm.get('applicationGroup')?.setValue(groupValue);

      const currentOneSignalBundleId = this.iosForm.get('oneSignalBundleId')?.value;
      if (!currentOneSignalBundleId) {
        const oneSignalBundleIdValue = `${newValue}.onesignal`;
        this.iosForm.get('oneSignalBundleId')?.setValue(oneSignalBundleIdValue);
      }
    });
  }

  getFileNameWithoutExtension(filename: string): string {
    if (!filename) return '';
    const index = filename.lastIndexOf('.');
    return filename.substring(0, index);
  }

  public onPlistUploadSuccess(file: File): void {
    this.uploadToRoot(file).subscribe({
      next: () => {
        const path = `${environment.azure.whitelabel.storageEndpoint}/${environment.azure.whitelabel.container}/${this.accountantId}/${IOS_CREDENTIALS_DEFAULT_FOLDER}/${file.name}`;
        this.iosForm.controls['googleServicePlistLink'].patchValue(path);

        this.toastrService.info('Google Service Plist enviado com sucesso');
      },
    });
  }

  public onAppProvisionProfileUploadSuccess(file: File) {
    this.uploadToCredentialsFolder(file).subscribe({
      next: () => {
        const path = `${environment.azure.whitelabel.storageEndpoint}/${environment.azure.whitelabel.container}/${this.accountantId}/${IOS_CREDENTIALS_DEFAULT_FOLDER}/${file.name}`;
        this.iosForm.controls['appProvisionProfileLink'].patchValue(path);

        const fileNameWithoutExtension = this.getFileNameWithoutExtension(
          file.name
        );
        this.iosForm.controls['appProvisionProfileSpecifier'].setValue(
          fileNameWithoutExtension
        );

        this.toastrService.info('App Provision Profile enviado com sucesso');
      },
    });
  }

  public onOneSignalProvisionProfileUploadSuccess(file: File) {
    this.uploadToCredentialsFolder(file).subscribe({
      next: () => {
        const path = `${environment.azure.whitelabel.storageEndpoint}/${environment.azure.whitelabel.container}/${this.accountantId}/${IOS_CREDENTIALS_DEFAULT_FOLDER}/${file.name}`;
        this.iosForm.controls['oneSignalProvisionProfileLink'].patchValue(path);

        const fileNameWithoutExtension = this.getFileNameWithoutExtension(
          file.name
        );
        this.iosForm.controls['onesignalProvisionProfileSpecifier'].setValue(
          fileNameWithoutExtension
        );

        this.toastrService.info(
          'OneSignal Provision Profile enviado com sucesso'
        );
      },
    });
  }

  public onCertificateUploadSuccess(file: File) {
    this.uploadToCredentialsFolder(file).subscribe({
      next: () => {
        const path = `${environment.azure.whitelabel.storageEndpoint}/${environment.azure.whitelabel.container}/${this.accountantId}/${IOS_CREDENTIALS_DEFAULT_FOLDER}/${file.name}`;
        this.iosForm.controls['p12CertificateLink'].patchValue(path);

        this.toastrService.info('Certificado enviado com sucesso');
      },
    });
  }

  public onIconsUploadSuccess(files: File[] | File) {
    if (Array.isArray(files)) {
      const path = `${this.accountantId}/${IOS_ICONS_DEFAULT_FOLDER}`;

      forkJoin(this.whitelabelUploadHelper.uploadMany(files, path)).subscribe({
        next: () => {
          const prefix = `${environment.azure.whitelabel.storageEndpoint}/${environment.azure.whitelabel.container}/${this.accountantId}/${IOS_ICONS_DEFAULT_FOLDER}`;
          const iconsLinks = this.normalizeUploadedIconsNamesToForm(
            files,
            files.length,
            prefix
          );

          this.iosForm.controls['iconsLink'].patchValue(iconsLinks);
          this.toastrService.info('Ícones enviados com sucesso');
        },
      });

      return;
    }

    this.uploadToRoot(files).subscribe({
      next: () => {
        const path = `${environment.azure.whitelabel.storageEndpoint}/${environment.azure.whitelabel.container}/${this.accountantId}/${files.name}`;

        this.iosForm.controls['appIconLink'].patchValue(path);
        this.toastrService.info('Ícone do App enviado com sucesso');
      },
    });
  }

  public onFileUploadError() {
    this.toastrService.error('Arquivo inválido');
  }

  public getControl(controlName: string): AbstractControl | null {
    const controlExists = !!this.iosForm.controls[controlName];
    if (!controlExists) return null;
    return this.iosForm.controls[controlName];
  }

  private handleAccountantId() {
    this.parentForm().controls['accountantId'].valueChanges.subscribe({
      next: () => {
        if (!this.parentForm().controls['accountantId'].valid) this.tabs = 0;
      },
    });
  }

  private handleDisableDraftModeControls() {
    Object.keys(this.iosForm.controls).forEach((control) => {
      if (control === 'iosCertificateExpiryDate') return;
      this.iosForm.controls[control].addValidators(Validators.required);
      this.iosForm.controls[control].updateValueAndValidity();
    });

    this.getControl('status')?.enable();
  }

  private normalizeUploadedIconsNamesToForm(
    icons: File[],
    totalLength: number,
    fileNamePrefixPath: string
  ): string {
    let path = '';

    icons.forEach((icon, index) => {
      if (totalLength - 1 !== index)
        path += `${fileNamePrefixPath}/${icon.name},`;
      else path += `${fileNamePrefixPath}/${icon.name}`;
    });

    return path;
  }

  private handleEnableDraftModeControls() {
    const controlsNameToKeepRequired = ['status'];
    const controls = Object.keys(this.iosForm.controls);

    const controlsToRemoveRequiredValidation = controls.filter(
      (control) => !controlsNameToKeepRequired.includes(control)
    );

    controlsToRemoveRequiredValidation.forEach((control) => {
      this.iosForm.controls[control].removeValidators(Validators.required);
      this.iosForm.controls[control].updateValueAndValidity();
    });

    this.getControl('status')?.patchValue(0);
    this.getControl('status')?.disable();
  }

  public get accountantId() {
    return this.parentForm().controls['accountantId']?.value;
  }

  private uploadToRoot(file: File): Observable<any> {
    const path = this.accountantId;
    return this.whitelabelUploadHelper.uploadSingle(file, path);
  }

  private uploadToCredentialsFolder(file: File): Observable<any> {
    const path = `${this.accountantId}/${IOS_CREDENTIALS_DEFAULT_FOLDER}`;
    return this.whitelabelUploadHelper.uploadSingle(file, path);
  }

  private updateIosValuesWithPreviousSavedConfigurations(
    savedConfigurations: IWhitelabelIosPropertiesConfiguration
  ) {
    this.getControl('developmentTeam')?.setValue(
      savedConfigurations.developmentTeam
    );
    this.getControl('iosBundleId')?.setValue(savedConfigurations.iosBundleId);
    this.getControl('applicationGroup')?.setValue(
      savedConfigurations.applicationGroup
    );
    this.getControl('apiKeyId')?.setValue(savedConfigurations.apiKeyId);
    this.getControl('apiKeyIssuerId')?.setValue(
      savedConfigurations.apiKeyIssuerId
    );
    this.getControl('apiTokenBase64')?.setValue(
      savedConfigurations.apiTokenBase64
    );
    this.getControl('appProvisionProfileSpecifier')?.setValue(
      savedConfigurations.provisionProfileSpecifier
    );
    this.getControl('appId')?.setValue(savedConfigurations.appId);
    this.getControl('status')?.setValue(savedConfigurations.status);

    this.getControl('oneSignalBundleId')?.setValue(
      savedConfigurations.oneSignal?.bundleId
    );
    this.getControl('onesignalProvisionProfileSpecifier')?.setValue(
      savedConfigurations.oneSignal?.provisionProfileSpecifier
    );

    this.getControl('iosCertificateExpiryDate')?.setValue(
      savedConfigurations.iosCertificateExpiryDate
    );

    this.updateFilesLinks();

    // if (savedConfigurations?.files) {
    //   this.getControl('appProvisionProfileLink')?.setValue(savedConfigurations.files.appProvisionProfile || null);
    //   this.getControl('oneSignalProvisionProfileLink')?.setValue(savedConfigurations.files.oneSignalProvisionProfile || null);
    //   this.getControl('p12CertificateLink')?.setValue(savedConfigurations.files.certificate || null);
    //   this.getControl('iconsLink')?.setValue(savedConfigurations.files.icons ? prepareIconsLinksToForm(savedConfigurations.files.icons) : null);
    //   this.getControl('appIconLink')?.setValue(savedConfigurations.files.appIcon || null);
    //   this.getControl('googleServicePlistLink')?.setValue(savedConfigurations.files.googleServicePlist || null);
    // }
  }

  //ToDo: Remover método quando todos os apps forem salvos novamente (com a prop. 'files' preenchida)
  private updateFilesLinks() {
    const rootFolder = `${environment.azure.whitelabel.storageEndpoint}/${environment.azure.whitelabel.container}/${this.accountantId}`;

    const credFolder = `${rootFolder}/${IOS_CREDENTIALS_DEFAULT_FOLDER}`;
    const iconsFolder = `${rootFolder}/${IOS_ICONS_DEFAULT_FOLDER}`;

    this.getControl('appProvisionProfileLink')?.setValue(
      `${credFolder}/distribution_App.mobileprovision`
    );
    this.getControl('oneSignalProvisionProfileLink')?.setValue(
      `${credFolder}/distribution_OneSignal.mobileprovision`
    );

    this.getControl('googleServicePlistLink')?.setValue(
      `${rootFolder}/GoogleService-Info.plist`
    );

    this.getControl('p12CertificateLink')?.setValue(
      `${credFolder}/distribution.p12`
    );

    this.getControl('appIconLink')?.setValue(`${rootFolder}/logo.png`);

    let iconsLinks: string[] = [];

    WhitelabelIosIcons.forEach((icon) => {
      iconsLinks.push(`${iconsFolder}/${icon}`);
    });

    this.getControl('iconsLink')?.setValue(prepareIconsLinksToForm(iconsLinks));
  }
}
